import { z } from "zod";
import { EventParticipantIdPublicSchema } from "./branded-id.schemas.js";
import {
  ParticipantReasonToMeetSchema,
  ReasonsToMeetInputHashesSchema,
} from "./participant-reasons-to-meet.schemas.js";

/**
 * A participant's computed reasons to meet for the admin inspector
 * (`GET /api/v1/admin/events/:eventIdPublic/participants/:participantIdPublic/reasons-to-meet`).
 *
 * Vectors stay behind: they are for search, and nothing on the page reads them.
 * `reasonsToMeet` is `null` when the participant's reasons have never been
 * computed; `pendingSince` and `runningSince` say whether a run is waiting or
 * in flight.
 */
export const AdminParticipantReasonsToMeetSchema = z.object({
  pendingSince: z.coerce.date().nullable(),
  runningSince: z.coerce.date().nullable(),
  reasonsToMeet: z
    .object({
      model: z.string(),
      dimensions: z.number(),
      hashes: ReasonsToMeetInputHashesSchema,
      reasons: z.array(ParticipantReasonToMeetSchema),
      computedAt: z.coerce.date(),
    })
    .nullable(),
});

export type AdminParticipantReasonsToMeet = z.infer<
  typeof AdminParticipantReasonsToMeetSchema
>;

/**
 * Which participants' reasons to meet to recompute
 * (`PUT /api/v1/admin/events/:eventIdPublic/reasons-to-meet/recompute`):
 * these, or `null` for everyone in the event. Unless `force`, a participant
 * whose inputs have not changed since their last computation keeps it.
 */
export const AdminRecomputeReasonsToMeetBodySchema = z.object({
  participantIdsPublic: z
    .array(EventParticipantIdPublicSchema)
    .min(1)
    .nullable(),
  force: z.boolean().default(false),
});

export type AdminRecomputeReasonsToMeetBody = z.infer<
  typeof AdminRecomputeReasonsToMeetBodySchema
>;
